import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { AuthContext } from './AuthContext';
import { accountService } from '../services/accountService';

/**
 * One shared account/tier read for the whole portal (EPIC-15). Before this,
 * ProtectedRoute, TierGate, Layout and every page each called
 * GET /api/user/account independently — loading /dashboard fired it three or
 * more times, and every paywall would have multiplied it. The server stays
 * the real gate (403 MAX_TIER_REQUIRED); this context only decides what to
 * render.
 *
 * `refresh()` re-reads after anything that changes entitlements (an upgrade
 * checkout, a seat claim) so paywalls unlock without a full reload.
 */

export interface TierState {
  account: any | null;
  /** 'pro' | 'max' — resolved server side; 'pro' until the account loads. */
  effectiveTier: 'pro' | 'max';
  isMax: boolean;
  isAdmin: boolean;
  /** True while the very first fetch for the current user is in flight. */
  loading: boolean;
  refresh: () => Promise<any | null>;
}

const defaultState: TierState = {
  account: null,
  effectiveTier: 'pro',
  isMax: false,
  isAdmin: false,
  loading: true,
  refresh: async () => null,
};

export const TierContext = createContext<TierState>(defaultState);

export function TierProvider({ children }: { children: React.ReactNode }) {
  const { user } = useContext(AuthContext);
  const [account, setAccount] = useState<any | null>(null);
  const [loading, setLoading] = useState(true);
  // Monotonic guard: a stale in-flight fetch (previous user, or an older
  // refresh) must never overwrite a newer result.
  const fetchSeq = useRef(0);

  const refresh = useCallback(async () => {
    const seq = ++fetchSeq.current;
    try {
      const data = await accountService.getAccountStatus();
      if (seq === fetchSeq.current) {
        setAccount(data);
        setLoading(false);
      }
      return data;
    } catch {
      if (seq === fetchSeq.current) {
        setAccount(null);
        setLoading(false);
      }
      return null;
    }
  }, []);

  useEffect(() => {
    if (!user) {
      fetchSeq.current++;
      setAccount(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    refresh();
  }, [user, refresh]);

  const value = useMemo<TierState>(() => {
    const effectiveTier: 'pro' | 'max' = account?.effectiveTier === 'max' ? 'max' : 'pro';
    return {
      account,
      effectiveTier,
      isMax: effectiveTier === 'max',
      isAdmin: !!account?.isAdmin,
      loading,
      refresh,
    };
  }, [account, loading, refresh]);

  return <TierContext.Provider value={value}>{children}</TierContext.Provider>;
}

export function useTier(): TierState {
  return useContext(TierContext);
}
